import { GenderEnum, UserTypeEnum } from '@matap/shared';
import { useCallback, useState } from 'react';
import { FlatList } from 'react-native';
import styled from 'styled-components/native';
import { actionSetFirstLogin, actionSetUser } from '@/redux';
import { useAppDispatch } from '@/hooks/use.redux';
import { useAssets } from '@/hooks/use.assets';
import { useUser } from '@/hooks/use.user';
import { Box } from '@/components/base/box';
import { AppImageView } from '@/components/base/app.image.view';
import { AppTextView } from '@/components/base/app.text.view';
import { AppButton } from '@/components/base/app.button';
import { AppDropdownMenu } from '@/components/compose/app.dropdown.menu';
import { AppTextField } from '@/components/base/app.text.field';
import { usePatchProfile } from '@/apis/user';
import { numbersToEnglish, wpVal } from '../../utils';

export function IntroView() {
  const assets = useAssets();
  const dispatch = useAppDispatch();
  const IntroData = {
    [UserTypeEnum.PATIENT]: [
      {
        id: 0,
        title: 'ارتباط تصویری مستقیم با پزشک',
        text: 'با اپلیکیشن مطپ می‌توانید از طریــق\nارتباط تصویری، صوتی و متنی به صورت\nمستقیم با دکتر خود در ارتباط باشید.',
        image: assets.images.intro.patient1,
      },
      {
        id: 1,
        title: 'دریافت نوبت حضوری',
        text: 'می‌توانید از طریق نوبت حضوری در \nمطپ از پزشکان منتخب در سراسر کشور\nنوبت بگیرید.',
        image: assets.images.intro.patient2,
      },
    ],
    [UserTypeEnum.DOCTOR]: [
      {
        id: 0,
        title: 'پیگیری‌های بعد از درمان بیماران',
        text: 'با اپلیکیشن مطپ می‌توانید از طریــق\nارتباط تصویری، صوتی و متنی به صورت\nمستقیم با بیمار خود در ارتباط باشید.',
        image: assets.images.intro.doctor1,
      },
      {
        id: 1,
        title: 'نوبت‌دهی حضوری به بیماران',
        text: 'بیماران می‌توانند از طریق دریافت نوبت\nحضوری در مطپ از پزشکان منتخب\nدر سراسر کشور نوبت بگیرند.',
        image: assets.images.intro.doctor2,
      },
    ],
  };

  const { user } = useUser();
  const data = IntroData[user.type];
  const [introSelected, setIntroSelected] = useState(0);
  const [showNameInput, setShowNameInput] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [genderInput, setGenderInput] = useState(user.gender || GenderEnum.UNKNOWN);
  const { mutate: updateProfile, isPending } = usePatchProfile({
    onSuccess: (data) => {
      dispatch(actionSetUser(data));
      dispatch(actionSetFirstLogin(false));
    },
  });

  const next = useCallback(() => {
    if (introSelected < data.length - 1) {
      setIntroSelected(introSelected + 1);
    } else {
      const shouldAskName = user.type === UserTypeEnum.PATIENT && (!user?.name || /^\d/.test(numbersToEnglish(user.name)));
      if (shouldAskName) {
        setShowNameInput(true);
        setGenderInput(user.gender || GenderEnum.UNKNOWN);
      } else {
        dispatch(actionSetFirstLogin(false));
      }
    }
  }, [data.length, dispatch, introSelected, user]);

  const handleSaveName = useCallback(() => {
    if (nameInput.trim()) {
      updateProfile({ body: { name: nameInput.trim(), gender: genderInput } });
    } else {
      dispatch(actionSetFirstLogin(false));
    }
  }, [nameInput, genderInput, updateProfile, dispatch]);

  if (showNameInput) {
    return (
      <Box flex={1} alignItems={'center'} justifyContent={'center'} style={{ paddingHorizontal: '10%' }}>
        <Box width={'100%'}>
          <IntroText bold textAlign={'center'} variant={'medium'} textColor={'faded'} text={'لطفاً نام و نام خانوادگی خود را وارد کنید'} />
          <NameInputWrapper>
            <AppTextField fullWidth placeholder={'نام و نام خانوادگی'} value={nameInput} onChangeText={setNameInput} />
          </NameInputWrapper>
          <GenderWrapper>
            <AppDropdownMenu
              label={'جنسیت:'}
              items={[
                { key: GenderEnum.MALE, label: 'آقا' },
                { key: GenderEnum.FEMALE, label: 'خانم' },
                { key: GenderEnum.UNKNOWN, label: 'نامشخص' },
              ]}
              selectedKey={genderInput}
              onSelect={(key) => setGenderInput(key as GenderEnum)}
            />
          </GenderWrapper>
          <IntroButton title={'تایید'} onPress={handleSaveName} shadow={'green'} loading={isPending} />
        </Box>
      </Box>
    );
  }

  return (
    <Container alignItems={'center'}>
      <Box fullWidth flexGrow={1} alignItems={'center'} justifyContent={'center'}>
        <IntroImage contentFit="contain" source={data[introSelected].image} />
      </Box>
      <Box fullWidth flexGrow={1} alignItems={'center'} justifyContent={'center'}>
        <Box>
          <IntroTitle bold textAlign={'center'} variant={'title'} textColor={'blue'} text={data[introSelected].title} />
          <IntroText bold textAlign={'center'} variant={'medium'} textColor={'faded'} text={data[introSelected].text} />
          <IntroPagination>
            <PaginationList
              horizontal
              data={data}
              renderItem={({ item }: any) => (
                <Box height={10} width={10} style={{ borderRadius: 100, marginHorizontal: 2, backgroundColor: item.id === introSelected ? '#38488A' : '#C4C4C4' }} />
              )}
            />
          </IntroPagination>
        </Box>
      </Box>
      <IntroButton title={introSelected >= data.length - 1 ? 'ورود' : 'بعدی'} onPress={() => next()} rounded fullWidth />
    </Container>
  );
}

const Container = styled(Box)`
  flex: 1;
  width: 100%;
  padding-left: ${wpVal(5)};
  padding-right: ${wpVal(5)};
`;

const IntroImage = styled(AppImageView)`
  width: ${({ theme }) => theme.hp(30)}px;
  height: ${({ theme }) => theme.hp(30)}px;
  align-self: center;
`;

const IntroTitle = styled(AppTextView)``;

const IntroText = styled(AppTextView)`
  margin-top: ${({ theme }) => theme.hp(3)}px;
  line-height: ${({ theme }) => theme.hp(4)}px;
`;

const IntroPagination = styled(Box)`
  align-self: center;
  margin-top: 8%;
  height: 20px;
`;

const IntroButton = styled(AppButton)`
  margin-bottom: ${({ theme }) => theme.hp(6)}px;
  width: auto;
  padding-horizontal: 55px;
`;

const PaginationList = styled(FlatList).attrs({
  contentContainerStyle: { justifyContent: 'space-between' },
})``;

const NameInputWrapper = styled(Box)`
  margin-top: ${({ theme }) => theme.hp(5)}px;
  margin-bottom: ${({ theme }) => theme.hp(2)}px;
`;

const GenderWrapper = styled(Box)`
  align-items: flex-end;
  margin-bottom: ${({ theme }) => theme.hp(3)}px;
`;
